import type http from 'node:http';
import type https from 'node:https';
import type { Socket } from 'node:net';

import type { RetryFunction } from 'async-retry';
import retry from 'async-retry';
import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axios, { AxiosHeaders } from 'axios';

import { APIFY_ENV_VARS } from '@apify/consts';
import type { Log } from '@apify/log';

import { ApifyApiError } from './apify_api_error';
import type { RequestInterceptorFunction } from './interceptors';
import { InvalidResponseBodyError, requestInterceptors, responseInterceptors } from './interceptors';
import type { Statistics } from './statistics';
import { asArray, cast, getVersionData, isNode, isStream } from './utils';

const { version } = getVersionData();

const RATE_LIMIT_EXCEEDED_STATUS_CODE = 429;

export class HttpClient {
    stats: Statistics;

    maxRetries: number;

    minDelayBetweenRetriesMillis: number;

    userProvidedRequestInterceptors: RequestInterceptorFunction[];

    logger: Log;

    timeoutMillis: number;

    httpAgent?: http.Agent;

    httpsAgent?: https.Agent;

    axios: AxiosInstance;

    workflowKey?: string;

    private nodeInitPromise?: Promise<void>;

    private userAgentSuffix?: string | string[];

    constructor(options: HttpClientOptions) {
        const { token } = options;
        this.stats = options.apifyClientStats;
        this.maxRetries = options.maxRetries;
        this.minDelayBetweenRetriesMillis = options.minDelayBetweenRetriesMillis;
        this.userProvidedRequestInterceptors = options.requestInterceptors;
        this.timeoutMillis = options.timeoutSecs * 1000;
        this.logger = options.logger;
        this.workflowKey = options.workflowKey || process.env[APIFY_ENV_VARS.WORKFLOW_KEY];
        this.userAgentSuffix = options.userAgentSuffix;
        this._onRequestRetry = this._onRequestRetry.bind(this);

        this.axios = axios.create({
            // Disable axios's built-in proxy handling since we're using custom agents
            proxy: false,
            paramsSerializer: (params) => {
                const formattedParams: [string, string][] = Object.entries<string | Date>(params)
                    .filter(([, value]) => value !== undefined)
                    .map(([key, value]) => {
                        if (value instanceof Date) {
                            return [key, value.toISOString()];
                        }
                        const updatedValue = typeof value === 'boolean' ? Number(value) : value;
                        return [key, String(updatedValue)];
                    });

                return new URLSearchParams(formattedParams).toString();
            },
            validateStatus: null,
            // Using interceptors for this functionality.
            transformRequest: undefined,
            transformResponse: undefined,
            responseType: 'arraybuffer',
            timeout: this.timeoutMillis,
            // maxBodyLength needs to be Infinity, because -1 falls back to a 10 MB default
            // from an axios subdependency - 'follow-redirects'
            maxBodyLength: Infinity,
            // maxContentLength must be -1, because Infinity will cause axios to run super slow
            // thanks to a bug that's now fixed, but not released yet https://github.com/axios/axios/pull/3738
            maxContentLength: -1,
        });

        // Clean all default headers because they only make a mess and their merging is difficult to understand and buggy.
        this.axios.defaults.headers = new AxiosHeaders() as any;

        // If workflow key is available, pass it as a header
        if (this.workflowKey) {
            this.axios.defaults.headers['X-Apify-Workflow-Key'] = this.workflowKey;
        }

        // Attach Authorization header for all requests if token was provided
        if (token) {
            this.axios.defaults.headers.Authorization = `Bearer ${token}`;
        }

        requestInterceptors.forEach((i) => this.axios.interceptors.request.use(i as any));
        this.userProvidedRequestInterceptors.forEach((i) => this.axios.interceptors.request.use(i as any));
        responseInterceptors.forEach((i) => this.axios.interceptors.response.use(i as any));
    }

    private async ensureNodeInit(): Promise<void> {
        if (!isNode()) return;

        this.nodeInitPromise ??= this.initNode();

        return this.nodeInitPromise;
    }

    private async initNode(): Promise<void> {
        if (!isNode()) return;

        const [{ ProxyAgent }, os] = await Promise.all([import('proxy-agent'), import('node:os')]);

        // We want to keep sockets alive for better performance.
        // Enhanced agent configuration based on agentkeepalive best practices:
        // - Nagle's algorithm disabled for lower latency
        // - Free socket timeout to prevent socket leaks
        // - LIFO scheduling to reuse recent sockets
        // - Socket TTL for connection freshness
        const agentOptions: http.AgentOptions & { scheduling?: 'lifo' | 'fifo' } = {
            keepAlive: true,
            // Timeout for inactive sockets
            // Prevents socket leaks from idle connections
            timeout: this.timeoutMillis,
            // Keep alive timeout for free sockets (15 seconds)
            // Node.js will close unused sockets after this period
            keepAliveMsecs: 15_000,
            // Maximum number of sockets per host
            maxSockets: 256,
            maxFreeSockets: 256,
            // LIFO scheduling - reuse most recently used sockets for better performance
            scheduling: 'lifo',
        };

        // Use ProxyAgent which automatically detects proxy from environment variables
        // and supports CONNECT tunneling
        const proxyAgent = new ProxyAgent(agentOptions);
        this.httpAgent = proxyAgent;
        this.httpsAgent = proxyAgent;

        // Disable Nagle's algorithm for lower latency
        // This sends data immediately instead of buffering small packets
        const setNoDelay = (socket: Socket) => {
            socket.setNoDelay(true);
        };

        this.httpAgent.on('socket', setNoDelay);
        this.httpsAgent.on('socket', setNoDelay);

        this.axios.defaults.httpAgent = this.httpAgent;
        this.axios.defaults.httpsAgent = this.httpsAgent;

        // Works only in Node. Cannot be set in browser
        const isAtHome = !!process.env[APIFY_ENV_VARS.IS_AT_HOME];
        let userAgent = `ApifyClient/${version} (${os.type()}; Node/${process.version}); isAtHome/${isAtHome}`;

        if (this.userAgentSuffix) {
            userAgent += `; ${asArray(this.userAgentSuffix).join('; ')}`;
        }

        this.axios.defaults.headers['User-Agent'] = userAgent;
    }

    async call<T = any>(config: ApifyRequestConfig): Promise<ApifyResponse<T>> {
        await this.ensureNodeInit();
        this.stats.calls++;
        const makeRequest = this._createRequestHandler(config);

        return retry(makeRequest, {
            retries: this.maxRetries,
            minTimeout: this.minDelayBetweenRetriesMillis,
            onRetry: this._onRequestRetry,
        });
    }

    private _informAboutStreamNoRetry() {
        this.logger.warningOnce(
            'Request body was a stream - retrying will not work, as part of it was already consumed.',
        );
        this.logger.warningOnce(
            'If you want Apify client to handle retries for you, collect the stream into a buffer before sending it.',
        );
    }

    /**
     * Successful responses are returned, errors and unsuccessful
     * status codes are retried. See the following functions for the
     * retrying logic.
     */
    private _createRequestHandler(config: ApifyRequestConfig) {
        const makeRequest: RetryFunction<ApifyResponse, Error> = async (stopTrying, attempt) => {
            this.stats.requests++;
            let response: ApifyResponse;
            const requestIsStream = isStream(config.data);

            try {
                if (requestIsStream) {
                    // Handling redirects is not possible without buffering - part of the stream has already been sent and can't be recovered
                    // when server sends the redirect. Therefore we need to override this in Axios config to prevent it from buffering the body.
                    // see also axios/axios#1045
                    config = { ...config, maxRedirects: 0 };
                }

                // Increase timeout with each attempt. Max timeout is bounded by the client timeout.
                config.timeout = Math.min(
                    this.timeoutMillis,
                    (config.timeout ?? this.timeoutMillis) * 2 ** (attempt - 1),
                );

                response = await this.axios.request(config);
                if (this._isStatusOk(response.status)) return response;
            } catch (err) {
                return cast(this._handleRequestError(err as AxiosError, config, stopTrying));
            }

            if (response.status === RATE_LIMIT_EXCEEDED_STATUS_CODE) {
                this.stats.addRateLimitError(attempt);
            }

            const apiError = new ApifyApiError(response, attempt);
            if (this._isStatusCodeRetryable(response.status)) {
                if (requestIsStream) {
                    this._informAboutStreamNoRetry();
                } else {
                    // allow a retry
                    throw apiError;
                }
            }
            stopTrying(apiError);

            return response;
        };

        return makeRequest;
    }

    private _isStatusOk(statusCode: number) {
        return statusCode < 300;
    }

    /**
     * Handles all unexpected errors that can happen, but are not
     * Apify API typed errors. E.g. network errors, timeouts and so on.
     */
    private _handleRequestError(err: AxiosError, config: ApifyRequestConfig, stopTrying: (e: Error) => void) {
        if (this._isTimeoutError(err) && config.doNotRetryTimeouts) {
            return stopTrying(err);
        }

        if (this._isRetryableError(err)) {
            if (isStream(config.data)) {
                this._informAboutStreamNoRetry();
            } else {
                throw err;
            }
        }
        return stopTrying(err);
    }

    /**
     * Axios calls req.abort() on timeouts so timeout errors will
     * have a code ECONNABORTED.
     */
    private _isTimeoutError(err: AxiosError) {
        return err.code === 'ECONNABORTED';
    }

    /**
     * We don't want to retry every exception thrown from Axios.
     * The common denominator for retryable errors are network issues.
     * @param {Error} err
     * @private
     */
    private _isRetryableError(err: AxiosError) {
        return this._isNetworkError(err) || this._isResponseBodyInvalid(err);
    }

    /**
     * When a network connection to our API is interrupted in the middle of streaming
     * a response, the request often does not fail, but simply contains
     * an incomplete response. This can often be fixed by retrying.
     */
    private _isResponseBodyInvalid(err: Error): err is InvalidResponseBodyError {
        return err instanceof InvalidResponseBodyError;
    }

    /**
     * When a network request is attempted by axios and fails,
     * it throws an AxiosError, which will have the request
     * and config (and other) properties.
     */
    private _isNetworkError(err: AxiosError) {
        const hasRequest = err.request && typeof err.request === 'object';
        const hasConfig = err.config && typeof err.config === 'object';
        return hasRequest && hasConfig;
    }

    /**
     * We retry 429 (rate limit) and 500+.
     * For status codes 300-499 (except 429) we do not retry the request,
     * because it's probably caused by invalid url (redirect 3xx) or invalid user input (4xx).
     */
    private _isStatusCodeRetryable(statusCode: number) {
        const isRateLimitError = statusCode === RATE_LIMIT_EXCEEDED_STATUS_CODE;
        const isInternalError = statusCode >= 500;
        return isRateLimitError || isInternalError;
    }

    private _onRequestRetry(error: Error, attempt: number) {
        if (attempt === Math.round(this.maxRetries / 2)) {
            this.logger.warning(
                `API request failed ${attempt} times. Max attempts: ${this.maxRetries + 1}.\nCause:${error.stack}`,
            );
        }
    }
}

export interface ApifyRequestConfig extends AxiosRequestConfig {
    stringifyFunctions?: boolean;
    forceBuffer?: boolean;
    doNotRetryTimeouts?: boolean;
}

export interface ApifyResponse<T = any> extends AxiosResponse<T> {
    config: ApifyRequestConfig & InternalAxiosRequestConfig;
}

export interface HttpClientOptions {
    apifyClientStats: Statistics;
    maxRetries: number;
    minDelayBetweenRetriesMillis: number;
    requestInterceptors: RequestInterceptorFunction[];
    timeoutSecs: number;
    logger: Log;
    token?: string;
    workflowKey?: string;
    /** @internal */
    userAgentSuffix?: string | string[];
}
